import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import WizardHeader from '../UnifiedIntake/WizardHeader';
import WizardProgressDots from '../UnifiedIntake/WizardProgressDots';
import WizardRating from '../UnifiedIntake/WizardRating';
import CurrencyInput from '../UnifiedIntake/CurrencyInput';
import ConsultantRetirementTimelineQuestion from './ConsultantRetirementTimelineQuestion';

const GET_IU_AND_QUESTIONS = gql`
  query ConsultantWizardIu($id: ID!, $key_prefix: String) {
    getInventoryUser(id: $id) {
      id
      retirement_timeline_bucket
      admin_assessment_snapshot
      user {
        id
      }
    }
    assessmentQuestions(key_prefix: $key_prefix) {
      id
      key
      text
      cepa_category
    }
  }
`;

const ADMIN_UPSERT_RETIREMENT_TIMELINE = gql`
  mutation AdminUpsertConsultantRetirementTimeline($inventory_user_id: ID!, $retirement_timeline_bucket: String!) {
    adminUpsertConsultantRetirementTimeline(inventory_user_id: $inventory_user_id, retirement_timeline_bucket: $retirement_timeline_bucket) {
      inventory_user {
        id
        retirement_timeline_bucket
      }
      errors
    }
  }
`;

const ADMIN_UPSERT_DIAG = gql`
  mutation AdminUpsertConsultantOwnerDiagnostic($inventory_user_id: ID!, $q_index: Int, $value: Int, $complete: Boolean) {
    adminUpsertConsultantOwnerDiagnostic(inventory_user_id: $inventory_user_id, q_index: $q_index, value: $value, complete: $complete) {
      inventory_user {
        id
        admin_assessment_snapshot
      }
      errors
    }
  }
`;

const ADMIN_UPSERT_PROFIT_GAP = gql`
  mutation AdminUpsertConsultantProfitGap($inventory_user_id: ID!, $recasted_ebitda: Float) {
    adminUpsertConsultantProfitGap(inventory_user_id: $inventory_user_id, recasted_ebitda: $recasted_ebitda) {
      inventory_user {
        id
        admin_assessment_snapshot
      }
      errors
    }
  }
`;

const ADMIN_UPSERT_WEALTH = gql`
  mutation AdminUpsertConsultantWealthGap($inventory_user_id: ID!, $goal_type: String, $goal_annual_income: Float, $goal_lump_sum: Float, $current_investable_assets: Float, $withdrawal_rate: Float) {
    adminUpsertConsultantWealthGap(inventory_user_id: $inventory_user_id, goal_type: $goal_type, goal_annual_income: $goal_annual_income, goal_lump_sum: $goal_lump_sum, current_investable_assets: $current_investable_assets, withdrawal_rate: $withdrawal_rate) {
      inventory_user {
        id
        admin_assessment_snapshot
      }
      errors
    }
  }
`;

const ADMIN_FINALIZE = gql`
  mutation AdminFinalizeConsultantAssessment($inventory_user_id: ID!) {
    adminFinalizeConsultantAssessment(inventory_user_id: $inventory_user_id) {
      inventory_user {
        id
        admin_assessment_snapshot
      }
      errors
    }
  }
`;

const Section = {
  RetirementTimeline: -1,
  OwnerDiagnostic: 0,
  ProfitGap: 1,
  WealthGap: 2,
};

const ConsultantWizardOverlay = ({ inventoryUserId, isOpen, onClose, className = '' }) => {
  const { data, loading, refetch } = useQuery(GET_IU_AND_QUESTIONS, {
    variables: { id: inventoryUserId, key_prefix: 'owner_q' },
    fetchPolicy: 'network-only',
  });
  const [upsertTimeline] = useMutation(ADMIN_UPSERT_RETIREMENT_TIMELINE);
  const [upsertDiag] = useMutation(ADMIN_UPSERT_DIAG);
  const [upsertProfit] = useMutation(ADMIN_UPSERT_PROFIT_GAP);
  const [upsertWealth] = useMutation(ADMIN_UPSERT_WEALTH);
  const [finalize] = useMutation(ADMIN_FINALIZE);

  const iu = data?.getInventoryUser || {};
  const snapshot = iu.admin_assessment_snapshot || {};
  const questions = data?.assessmentQuestions || [];

  const [section, setSection] = useState(Section.RetirementTimeline);
  const [timelineBucket, setTimelineBucket] = useState(null);
  const [qIndex, setQIndex] = useState(0);
  const [ebitda, setEbitda] = useState('');
  const [annualIncome, setAnnualIncome] = useState('');
  const [investableAssets, setInvestableAssets] = useState('');
  const [goalType, setGoalType] = useState('income');
  const [goalLumpSum, setGoalLumpSum] = useState('');
  const [withdrawalRate, setWithdrawalRate] = useState(4);
  const [wealthStep, setWealthStep] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Initialize section based on existing data
  useEffect(() => {
    // Don't initialize if already showing results or if assessment is finalized
    if (!isOpen || initialized || loading || showResults) return;
    
    // Check if assessment is already finalized
    const isFinalized = snapshot?.meta?.finalized_at != null;
    if (isFinalized) {
      // Assessment is complete, show results screen
      setShowResults(true);
      setInitialized(true);
      return;
    }
    
    const existingBucket = iu.retirement_timeline_bucket;
    const od = snapshot.owner_diagnostic || {};
    const owner = Array.isArray(od.owner_answers) ? od.owner_answers : Array(6).fill(null);
    const business = Array.isArray(od.business_answers) ? od.business_answers : Array(6).fill(null);

    if (!existingBucket) {
      setSection(Section.RetirementTimeline);
    } else if (owner.length < 6 || business.length < 6 || owner.some(v => !v) || business.some(v => !v)) {
      setSection(Section.OwnerDiagnostic);
      const allAnswers = [...owner, ...business];
      const firstIncomplete = allAnswers.findIndex(v => !v);
      setQIndex(firstIncomplete >= 0 ? firstIncomplete : 0);
    } else if (!snapshot.profit_gap?.recasted_ebitda) {
      setSection(Section.ProfitGap);
      if (snapshot.profit_gap?.recasted_ebitda) setEbitda(String(snapshot.profit_gap.recasted_ebitda));
    } else {
      setSection(Section.WealthGap);
      const wg = snapshot.wealth_gap || {};
      if (wg.goal_type) setGoalType(wg.goal_type);
      if (wg.goal_annual_income) setAnnualIncome(String(wg.goal_annual_income));
      if (wg.goal_lump_sum) setGoalLumpSum(String(wg.goal_lump_sum));
      if (wg.current_investable_assets) setInvestableAssets(String(wg.current_investable_assets));
      if (wg.withdrawal_rate) setWithdrawalRate(Math.round(Number(wg.withdrawal_rate) * 100));
    }
    setInitialized(true);
  }, [isOpen, iu, snapshot, initialized, loading, showResults]);

  useEffect(() => {
    const root = document.documentElement;
    if (isOpen) root.classList.add('intake-overlay-active');
    return () => root.classList.remove('intake-overlay-active');
  }, [isOpen]);

  // Ensure showResults stays true if assessment is finalized
  useEffect(() => {
    const isFinalized = snapshot?.meta?.finalized_at != null;
    if (isFinalized && !showResults) {
      setShowResults(true);
    }
  }, [snapshot, showResults]);

  const totalDots = 16; // 1 timeline + 12 diagnostic + 1 EBITDA + 2 wealth = 16
  const currentDotIndex = (() => {
    if (section === Section.RetirementTimeline) return 0;
    if (section === Section.OwnerDiagnostic) return qIndex + 1;
    if (section === Section.ProfitGap) return 13;
    return wealthStep === 0 ? 14 : 15;
  })();
  const percent = Math.round(((currentDotIndex + 1) / totalDots) * 100);

  const handleTimelineSubmit = async () => {
    if (!timelineBucket) return;
    const map = { one_to_three: 'one_to_three', four_to_six: 'four_to_six', seven_plus: 'seven_plus' };
    await upsertTimeline({ variables: { inventory_user_id: inventoryUserId, retirement_timeline_bucket: map[timelineBucket] } });
    setSection(Section.OwnerDiagnostic);
    refetch();
  };

  const handleRating = async (val) => {
    const index = Math.min(Math.max(qIndex, 0), 11);
    await upsertDiag({ variables: { inventory_user_id: inventoryUserId, q_index: index, value: val } });
    const next = index + 1;
    if (next > 11) {
      try { await upsertDiag({ variables: { inventory_user_id: inventoryUserId, complete: true } }); } catch (_) {}
      setSection(Section.ProfitGap);
    } else {
      setQIndex(next);
    }
    refetch();
  };

  const saveProfitGap = async () => {
    const raw = parseFloat(String(ebitda).replace(/[^0-9.-]/g, ''));
    await upsertProfit({ variables: { inventory_user_id: inventoryUserId, recasted_ebitda: isFinite(raw) ? raw : null } });
    setSection(Section.WealthGap);
    refetch();
  };

  const saveWealthGap = async () => {
    setIsProcessing(true);
    const income = parseFloat(String(annualIncome).replace(/[^0-9.-]/g, ''));
    const assets = parseFloat(String(investableAssets).replace(/[^0-9.-]/g, ''));
    const lump = parseFloat(String(goalLumpSum).replace(/[^0-9.-]/g, ''));
    await upsertWealth({ variables: {
      inventory_user_id: inventoryUserId,
      goal_type: goalType,
      goal_lump_sum: goalType === 'lump_sum' && isFinite(lump) ? lump : null,
      goal_annual_income: goalType === 'income' && isFinite(income) ? income : null,
      withdrawal_rate: Number(withdrawalRate) / 100,
      current_investable_assets: isFinite(assets) ? assets : 0,
    }});
    try {
      const result = await finalize({ variables: { inventory_user_id: inventoryUserId } });
      if (result?.data?.adminFinalizeConsultantAssessment?.errors?.length > 0) {
        console.error('Finalization errors:', result.data.adminFinalizeConsultantAssessment.errors);
        // Still show success screen, but log errors
      }
      // Refetch inventory user data to get updated snapshot
      await refetch();
      // Ensure showResults stays true after refetch
      setIsProcessing(false);
      setShowResults(true);
    } catch (error) {
      console.error('Finalization error:', error);
      // Show error but don't close wizard - let user see what happened
      setIsProcessing(false);
      setShowResults(true);
    }
  };

  if (!isOpen) return null;
  // Don't return null if we're showing results or processing - preserve state
  if (loading && !showResults && !isProcessing) return null;

  const renderContent = () => {
    if (isProcessing || showResults) {
      return (
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="text-center space-y-8 max-w-2xl">
            {isProcessing ? (
              <>
                <style>{`@keyframes fb-spin{to{transform:rotate(360deg)}}`}</style>
                <div className="w-24 h-24 bg-succession-primary rounded-full flex items-center justify-center mx-auto">
                  <div className="w-12 h-12" style={{ animation: 'fb-spin 1s linear infinite' }} aria-hidden="true">
                    <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-4a6 6 0 0 0-6-6V2z" />
                    </svg>
                  </div>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-succession-foreground">Processing Assessment!</h1>
                <p className="text-xl text-succession-text-primary">Standby while we finalize the assessment.</p>
              </>
            ) : showResults ? (
              <>
                <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-succession-foreground">Assessment Complete!</h1>
                <p className="text-xl text-succession-text-primary mb-6">The consultant assessment has been finalized and saved.</p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  {iu.user?.id && (
                    <button 
                      type="button" 
                      className="px-8 py-4 bg-succession-primary text-white rounded-xl font-semibold hover:bg-succession-primary/90"
                      onClick={() => {
                        window.location.href = `/succession/consultant-assessment-report/${inventoryUserId}`;
                      }}
                    >
                      View Report
                    </button>
                  )}
                  {/* Download PDF button - placeholder for future feature */}
                  {/* <button 
                    type="button" 
                    className="px-8 py-4 border border-succession-border text-succession-text-primary rounded-xl font-semibold hover:bg-succession-bg-secondary"
                    onClick={() => {
                      // TODO: Implement PDF download
                      alert('PDF download coming soon!');
                    }}
                  >
                    Download PDF
                  </button> */}
                  <button 
                    type="button" 
                    className="px-8 py-4 border border-succession-border text-succession-text-primary rounded-xl font-semibold hover:bg-succession-bg-secondary"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      );
    }

    if (section === Section.RetirementTimeline) {
      return (
        <div className="pt-24 pb-32 px-4 min-h-screen flex flex-col bg-succession-bg-primary">
          <div className="max-w-3xl mx-auto flex-1 flex items-center justify-center">
            <div className="w-full">
              <div className="text-center mb-4">
                <span className="bg-yellow-200 text-yellow-900 px-3 py-1 rounded-md text-sm font-semibold">Consulting Mode</span>
              </div>
              <ConsultantRetirementTimelineQuestion
                value={timelineBucket}
                onChange={setTimelineBucket}
                onSubmit={handleTimelineSubmit}
              />
            </div>
          </div>
        </div>
      );
    }

    if (section === Section.OwnerDiagnostic) {
      const title = 'Owner Readiness & Buyer Attractiveness';
      const left = `Question ${qIndex + 1} of 12`;
      const right = 'Owner Readiness';
      const od = snapshot.owner_diagnostic || {};
      const owner = Array.isArray(od.owner_answers) ? od.owner_answers : Array(6).fill(null);
      const business = Array.isArray(od.business_answers) ? od.business_answers : Array(6).fill(null);
      const val = qIndex < 6 ? owner[qIndex] : business[qIndex - 6];
      return (
        <div className="pt-24 pb-32 px-4 min-h-screen flex flex-col bg-succession-bg-primary">
          <WizardHeader title={title} progress={percent} leftText={left} rightText={right} onClose={onClose} />
          <div className="max-w-3xl mx-auto flex-1">
            <div className="text-center mb-6">
              <div className="mb-2">
                <span className="bg-yellow-200 text-yellow-900 px-3 py-1 rounded-md text-sm font-semibold">Consulting Mode</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-succession-foreground">{questions[qIndex]?.text || 'Please rate your agreement'}</h1>
              <p className="text-succession-text-primary">Rate from 1 (strongly disagree) to 6 (strongly agree)</p>
            </div>
            <WizardRating value={val ?? null} onChange={handleRating} />
            <div className="mt-8" />
          </div>
          <div className="mt-auto py-6 border-t border-succession-border bg-succession-surface/90">
            <div className="max-w-3xl mx-auto flex items-center justify-between">
              <button type="button" className="px-4 py-2 rounded-xl border border-succession-border text-succession-foreground disabled:opacity-50" onClick={() => { if (qIndex > 0) setQIndex(qIndex - 1); }} disabled={qIndex === 0}>Previous</button>
              <WizardProgressDots total={totalDots} current={currentDotIndex} />
              <button type="button" className="px-4 py-2 rounded-xl bg-succession-primary text-white" onClick={() => { if (qIndex < 11) setQIndex(qIndex + 1); else setSection(Section.ProfitGap); }}>Next</button>
            </div>
          </div>
        </div>
      );
    }

    if (section === Section.ProfitGap) {
      const title = 'Normalized EBITDA';
      const left = 'Understanding Your Gaps';
      const right = 'Normalized EBITDA';
      return (
        <div className="pt-24 pb-32 px-4 min-h-screen flex flex-col bg-succession-bg-primary">
          <WizardHeader title={title} progress={percent} leftText={left} rightText={right} onClose={onClose} />
          <div className="max-w-3xl mx-auto flex-1">
            <div className="text-center mb-6">
              <div className="mb-2">
                <span className="bg-yellow-200 text-yellow-900 px-3 py-1 rounded-md text-sm font-semibold">Consulting Mode</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-succession-foreground">What is your normalized EBITDA?</h1>
              <p className="text-succession-text-primary">This is your profit adjusted for unusual or one-time expenses, giving a clear picture of what your business really earns.</p>
            </div>
            <CurrencyInput value={ebitda} onChange={setEbitda} />
          </div>
          <div className="mt-auto py-6 border-t border-succession-border bg-succession-surface/90">
            <div className="max-w-3xl mx-auto flex items-center justify-between">
              <button type="button" className="px-4 py-2 rounded-xl border border-succession-border text-succession-foreground" onClick={() => setSection(Section.OwnerDiagnostic)}>Previous</button>
              <WizardProgressDots total={totalDots} current={currentDotIndex} />
              <button type="button" className="bg-succession-primary text-white px-6 py-3 rounded-xl" onClick={saveProfitGap}>Next</button>
            </div>
          </div>
        </div>
      );
    }

    const title = 'Wealth Gap Inputs';
    const left = 'Understanding Your Gaps';
    const right = 'Wealth Gap';
    return (
      <div className="pt-24 pb-32 px-4 min-h-screen flex flex-col bg-succession-bg-primary">
        <WizardHeader title={title} progress={percent} leftText={left} rightText={right} onClose={onClose} />
        <div className="max-w-3xl mx-auto flex-1">
          <div className="text-center mb-4">
            <div className="mb-2">
              <span className="bg-yellow-200 text-yellow-900 px-3 py-1 rounded-md text-sm font-semibold">Consulting Mode</span>
            </div>
          </div>
          {wealthStep === 0 ? (
            <>
              <div className="text-center mb-4">
                <h1 className="text-2xl md:text-3xl font-semibold text-succession-foreground">What is your desired financial goal?</h1>
                <p className="text-succession-text-primary">Choose a lump-sum wealth target or an annual (pre-tax) income target. We'll use this to calculate your Wealth Gap.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <button type="button" onClick={() => setGoalType('lump_sum')} className={`px-6 py-3 rounded-xl font-semibold transition-all ${goalType === 'lump_sum' ? 'bg-succession-primary text-white' : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400'}`}>Lump Sum Wealth Goal</button>
                <button type="button" onClick={() => setGoalType('income')} className={`px-6 py-3 rounded-xl font-semibold transition-all ${goalType === 'income' ? 'bg-succession-primary text-white' : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400'}`}>Annual Income Target</button>
              </div>
            </>
          ) : (
            <div className="text-center mb-4">
              <h1 className="text-2xl md:text-3xl font-semibold text-succession-foreground">What is the total value of your investable (liquid) assets?</h1>
            </div>
          )}

          {wealthStep === 0 ? (
            <>
              {goalType === 'lump_sum' ? (
                <>
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-semibold text-succession-foreground">What lump-sum wealth are you aiming for?</h2>
                  </div>
                  <CurrencyInput value={goalLumpSum} onChange={setGoalLumpSum} />
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-semibold text-succession-foreground">What annual income are you targeting (pre-tax)?</h2>
                  </div>
                  <CurrencyInput value={annualIncome} onChange={setAnnualIncome} />
                  <div className="mt-8">
                    <p className="text-center text-succession-text-primary mb-3">Select your preferred annual withdrawal rate</p>
                    <div className="flex items-center justify-center gap-4">
                      {[3,4,5,6].map(r => (
                        <button key={r} type="button" onClick={() => setWithdrawalRate(r)} className={`px-4 py-3 rounded-xl border ${withdrawalRate===r ? 'bg-succession-primary text-white border-succession-primary' : 'border-gray-300 text-succession-foreground bg-white hover:border-gray-400'}`}>{r}%</button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <p className="text-succession-text-primary">This includes cash, savings, investments, and other assets you could readily use to fund your future goals. Do not include illiquid assets such as real estate or business equity.</p>
              </div>
              <CurrencyInput value={investableAssets} onChange={setInvestableAssets} />
            </>
          )}
        </div>
        <div className="mt-auto py-6 border-t border-succession-border bg-succession-surface/90">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <button type="button" className="px-4 py-2 rounded-xl border border-succession-border text-succession-foreground" onClick={() => { if (wealthStep === 0) setSection(Section.ProfitGap); else setWealthStep(0); }}>Previous</button>
            <WizardProgressDots total={totalDots} current={currentDotIndex} />
            {wealthStep === 0 ? (
              <button type="button" className="bg-succession-primary text-white px-6 py-3 rounded-xl" onClick={() => setWealthStep(1)}>Next</button>
            ) : (
              <button type="button" className="bg-succession-primary text-white px-6 py-3 rounded-xl" onClick={saveWealthGap}>Finish</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`fixed inset-0 z-50 bg-succession-bg-primary text-succession-foreground ${className}`} role="dialog" aria-modal="true">
      {renderContent()}
    </div>
  );
};

export default ConsultantWizardOverlay;

